import React from 'react';
import { usePlayer } from '../PlayerProvider';
import { brand } from '@/theme/colors';

interface VinylRecordProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showLabel?: boolean;
}

const VinylRecord: React.FC<VinylRecordProps> = ({ 
  size = 'medium', 
  className = '',
  showLabel = true 
}) => {
  const { isPlaying, currentVideo, playVideo, pauseVideo } = usePlayer();
  
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
  };
  
  const handleClick = () => {
    if (currentVideo) {
      if (isPlaying) {
        pauseVideo();
      } else {
        const videoWithDates = {
          ...currentVideo,
          createdAt: new Date(currentVideo.createdAt),
          updatedAt: new Date(currentVideo.updatedAt),
          publishedAt: new Date(currentVideo.publishedAt),
          dateAdded: new Date(currentVideo.dateAdded),
        };
        playVideo(videoWithDates);
      }
    }
  };
  
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <button
        onClick={handleClick}
        disabled={!currentVideo}
        className={`
          vinyl-record relative rounded-full cursor-pointer transition-all duration-300
          ${sizeClasses[size]}
          ${isPlaying ? 'animate-spin-slow' : ''}
          ${currentVideo ? 'hover:scale-110' : 'opacity-50 cursor-not-allowed'}
          focus:outline-none focus:ring-2 focus:ring-meradio-primary focus:ring-opacity-50
        `}
        style={{
          background: `radial-gradient(circle at 30% 30%, ${brand.dark} 0%, ${brand.darkLight} 40%, ${brand.dark} 70%, #000 100%)`,
          animationDuration: '3s',
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        <div className="absolute inset-1 rounded-full border border-neutral-600 opacity-30" />
        <div className="absolute inset-2 rounded-full border border-neutral-600 opacity-20" />
        <div className="absolute inset-3 rounded-full border border-neutral-600 opacity-10" />
        
        <div
          className="
            absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
            bg-meradio-primary rounded-full flex items-center justify-center
            shadow-inner
          "
          style={{
            width: size === 'small' ? '16px' : size === 'medium' ? '20px' : '24px',
            height: size === 'small' ? '16px' : size === 'medium' ? '20px' : '24px',
          }}
        >
          <div
            className="bg-meradio-dark rounded-full"
            style={{
              width: size === 'small' ? '4px' : size === 'medium' ? '6px' : '8px',
              height: size === 'small' ? '4px' : size === 'medium' ? '6px' : '8px',
            }}
          />
        </div>

        <div className={`
          absolute inset-0 flex items-center justify-center
          text-white opacity-0 hover:opacity-100 transition-opacity duration-200
          ${size === 'small' ? 'text-xs' : size === 'medium' ? 'text-sm' : 'text-base'}
        `}>
          {isPlaying ? '⏸️' : '▶️'}
        </div>
      </button>

      {showLabel && currentVideo && (
        <div className="flex-1 min-w-0">
          <div className={`
            text-white font-medium truncate
            ${size === 'small' ? 'text-xs' : size === 'medium' ? 'text-sm' : 'text-base'}
          `}>
            {currentVideo.title}
          </div>
          {size !== 'small' && (
            <div className="text-neutral-400 text-xs truncate">
              Now Playing
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }

        .vinyl-record:not(.animate-spin-slow) {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default VinylRecord;
